import React, { useState, useEffect, useCallback } from 'react';
import { tcgApi } from '../../api/client';
import { useTcgSets } from '../../hooks/useTcgSets';
import { formatPrice } from '../../utils/formatters';
import Pagination from '../common/Pagination';

export default function TcgCardPickerModal({
  onSelect, onClose, initialSearch,
  initialArtist = '', initialExtension = '', initialRarity = '',
  cardImageUrl = null, cardTitle = '', cardPrice = null, cardCurrency = 'EUR',
}) {
  const [name, setName] = useState(initialSearch || '');
  const [setId, setSetId] = useState('');
  const [artist, setArtist] = useState(initialArtist);
  const [rarity, setRarity] = useState(initialRarity);
  const [language, setLanguage] = useState('en'); // "en", "ja", or "" (all)
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const { sets } = useTcgSets();

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (name) params.name = name;
      if (setId) params.set_id = setId;
      if (artist) params.artist = artist;
      if (rarity) params.rarity = rarity;
      if (language) params.language = language;
      const res = await tcgApi.listCards(params);
      setResults(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [name, setId, artist, rarity, language, page]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when criteria change
  useEffect(() => { setPage(1); }, [name, setId, artist, rarity, language]);

  // Filter sets by language for dropdown
  const filteredSets = language
    ? sets.filter((s) => s.language === language)
    : sets;

  // Group filtered sets by series for dropdown
  const seriesGroups = {};
  filteredSets.forEach((s) => {
    if (!seriesGroups[s.series]) seriesGroups[s.series] = [];
    seriesGroups[s.series].push(s);
  });

  // Auto-select set from initialExtension on first render
  useEffect(() => {
    if (initialExtension && sets.length > 0 && !setId) {
      const extLower = initialExtension.toLowerCase();
      const match = sets.find((s) => s.name.toLowerCase() === extLower);
      if (match) {
        setSetId(match.id);
        // Auto-switch language if matched set is Japanese
        if (match.language === 'ja') {
          setLanguage('ja');
        }
      }
    }
  }, [sets]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect language when set changes
  const handleSetChange = (newSetId) => {
    setSetId(newSetId);
    if (newSetId) {
      const matchedSet = sets.find((s) => s.id === newSetId);
      if (matchedSet && matchedSet.language === 'ja' && language !== 'ja') {
        setLanguage('ja');
      } else if (matchedSet && matchedSet.language === 'en' && language !== 'en') {
        setLanguage('en');
      }
    }
  };

  // When language changes, clear set selection if it doesn't match
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    if (setId && newLang) {
      const matchedSet = sets.find((s) => s.id === setId);
      if (matchedSet && matchedSet.language !== newLang) {
        setSetId('');
      }
    }
  };

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected.id);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tcg-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Link to TCG Reference Card</h3>
          <button className="btn btn-sm" onClick={onClose}>&times;</button>
        </div>

        <div className="tcg-picker-search">
          <div className="filter-row">
            <div className="filter-field">
              <label>Card Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Search by name..."
                autoFocus
              />
            </div>
            <div className="filter-field">
              <label>Language</label>
              <select value={language} onChange={(e) => handleLanguageChange(e.target.value)}>
                <option value="">All</option>
                <option value="en">English / French</option>
                <option value="ja">Japanese</option>
              </select>
            </div>
            <div className="filter-field">
              <label>Set</label>
              <select value={setId} onChange={(e) => handleSetChange(e.target.value)}>
                <option value="">All Sets</option>
                {Object.entries(seriesGroups).map(([series, seriesSets]) => (
                  <optgroup key={series} label={series}>
                    {seriesSets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.total || s.printed_total})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="filter-field">
              <label>Illustrator</label>
              <input
                type="text"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="Search by artist..."
              />
            </div>
            <div className="filter-field">
              <label>Rarity</label>
              <input
                type="text"
                value={rarity}
                onChange={(e) => setRarity(e.target.value)}
                placeholder="e.g. Rare, Common..."
              />
            </div>
          </div>
        </div>

        <div className="tcg-picker-body">
          {/* Reference image of the scraped card */}
          {cardImageUrl && (
            <div className="tcg-picker-reference">
              <div className="tcg-picker-reference-label">Your card</div>
              <img
                src={cardImageUrl.replace(/s-l\d+/, 's-l500')}
                alt={cardTitle}
                className="tcg-picker-reference-img"
              />
              {cardTitle && (
                <div className="tcg-picker-reference-title" title={cardTitle}>
                  {cardTitle.length > 40 ? cardTitle.slice(0, 40) + '…' : cardTitle}
                </div>
              )}
              {cardPrice != null && (
                <div className="tcg-picker-reference-price">
                  {formatPrice(cardPrice, cardCurrency)}
                </div>
              )}
            </div>
          )}

          {/* TCG reference results */}
          <div className="tcg-picker-results">
            {loading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : results.length === 0 ? (
              <div className="empty-state"><p>No TCG cards found.</p></div>
            ) : (
              <div className="tcg-picker-grid">
                {results.map((card) => (
                  <div
                    key={card.id}
                    className={`tcg-picker-card ${selected?.id === card.id ? 'selected' : ''}`}
                    onClick={() => setSelected(card)}
                  >
                    {card.image_small ? (
                      <img src={card.image_small} alt={card.name} loading="lazy" />
                    ) : (
                      <div className="card-thumbnail-placeholder">?</div>
                    )}
                    <div className="tcg-picker-card-info">
                      <span className="tcg-picker-card-name">{card.name}</span>
                      {card.fr_name && card.fr_name !== card.name && (
                        <span className="tcg-picker-card-meta tcg-picker-card-fr">{card.fr_name}</span>
                      )}
                      <span className="tcg-picker-card-meta">
                        {card.set_id} #{card.number}
                        {card.rarity ? ` · ${card.rarity}` : ''}
                      </span>
                      {card.artist && (
                        <span className="tcg-picker-card-meta">{card.artist}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {pages > 1 && (
              <Pagination page={page} pages={pages} total={total} onPageChange={setPage} />
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!selected}
            onClick={handleConfirm}
          >
            Link Card
          </button>
        </div>
      </div>
    </div>
  );
}
